import React, { useCallback, useEffect, useMemo, useState } from 'react'
import TableCell from '@material-ui/core/TableCell'

import PropTypes from 'prop-types'
import TableContainer from '@material-ui/core/TableContainer'
import Table from '@material-ui/core/Table'
import TableRow from '@material-ui/core/TableRow'
import TableBody from '@material-ui/core/TableBody'
import styled from 'styled-components'
import CircularProgress from '@material-ui/core/CircularProgress'
import Typography from '@material-ui/core/Typography'
import moment from 'moment'
import {
  ApprovalsPeopleContainer,
  ApprovalViewContainer,
  BudgetContainer,
  DetailsActionContainer,
  StyledApproveButton,
  StyledMutedButton,
  StyledWeekContainer,
  TableWrapper,
  WeekContainer,
} from '../Approvals/ApprovalView.styles'
import { ProjectsTableContainer, StyledTableHead } from '../Approvals/ProjectsView.styles'
import { WeekTabButton } from '../Calendar/Header/styles'
import { calculateBudgetAndPacingInWeek, groupTimeEntries } from '../Approvals/Approvals.utils'
import ProjectSelection from '../../components/ProjectSelection'
import { LoadingIndicator } from '../Calendar/style'
import useWipData from '../WorkInProgress/useWipData'
import ApprovalProjectSelector from '../Approvals/Components/ApprovalProjectSelector'
import { getExpensesAvailableWeeks } from './expensesWeekUtils'

const ApprovalsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
`

document.body.style.overflow = 'auto'

const ExpensesView = ({
  projects,
  client,
  expenses,
  timeEntries: rawTimeEntries,
  timesheets,
  people,
  person,
  availableWeeks: allWeeks = [],
  rates,
  onProjectSelect,
  selectedProject,
  onApproveExpenseBillingStatus,
}) => {
  const projectExpenses = useMemo(
    () =>
      selectedProject ? expenses.filter(expense => expense.projectId === selectedProject._id) : [],
    [selectedProject, expenses],
  )

  const availableWeeks = useMemo(() => getExpensesAvailableWeeks(projectExpenses), [
    projectExpenses,
  ])

  const [selectedWeek, setSelectedWeek] = useState(availableWeeks[0])
  useEffect(() => {
    if (selectedWeek === undefined) setSelectedWeek(availableWeeks[0])
  }, [availableWeeks])

  useEffect(() => {
    setSelectedWeek(availableWeeks[0])
  }, [selectedProject])

  const selectedWeekExpenses = useMemo(
    () =>
      projectExpenses.filter(expense => {
        if (!selectedWeek) return false
        // Compare against the same locale week-start used to build availableWeeks
        // (see getExpensesAvailableWeeks) rather than ISO week numbers, which use a
        // different week-start day and can put the two out of sync.
        return moment(expense.incurredDate)
          .startOf('week')
          .isSame(selectedWeek.start, 'day')
      }),
    [projectExpenses, selectedWeek],
  )

  const selectedWeekPendingExpenses = useMemo(
    () =>
      selectedWeekExpenses.filter(expense => {
        if (!expense.approveState) return true
        return expense.approveState.status === 'pending'
      }),
    [selectedWeekExpenses],
  )

  const timeEntries = useMemo(() => {
    return groupTimeEntries(rawTimeEntries, timesheets.submitted, allWeeks)
  }, [timesheets, rawTimeEntries, allWeeks])

  const setProject = id => {
    onProjectSelect(projects.find(project => project._id === id))
  }

  const availableMonths = useMemo(() => {
    return Array.from(
      new Set(availableWeeks.map(week => moment(week.start).format('YYYY-MM'))),
    ).join(',')
  }, [availableWeeks])

  const selectedMonth = useMemo(() => {
    return selectedWeek?.start.format('YYYY-MM')
  }, [selectedWeek])

  const projectsArrayWithOnlySelectedProject = useMemo(() => {
    return selectedProject ? [selectedProject] : []
  }, [selectedProject])

  const { selectedMonthProjectWipReport: wipReport } = useWipData({
    client,
    projects: projectsArrayWithOnlySelectedProject,
    selectedProject,
    months: availableMonths,
    selectedMonth,
  })

  const peopleEntries =
    (timeEntries &&
      selectedProject &&
      selectedWeek &&
      timeEntries[selectedProject?._id]?.[selectedWeek?.stringWeek]) ||
    {}

  const { actualPacing, actualUsed, weekBudget } = useMemo(() => {
    if (!selectedProject || !selectedWeek || !wipReport) return {}
    return calculateBudgetAndPacingInWeek(
      selectedWeek.start,
      selectedProject,
      wipReport,
      availableWeeks,
    )
  }, [selectedProject, selectedWeek, wipReport, availableWeeks])

  if (!selectedProject)
    return (
      <ApprovalsContainer>
        <ApprovalViewContainer>
          <div>
            <ApprovalProjectSelector
              projects={projects}
              selectedProject={selectedProject}
              onSelectProject={onProjectSelect}
            />
          </div>
        </ApprovalViewContainer>
      </ApprovalsContainer>
    )

  return (
    <ApprovalsContainer data-testid="expensesView.root">
      <ApprovalViewContainer>
        <div>
          {selectedProject && (
            <ProjectSelection
              projects={projects}
              selectedProject={selectedProject?._id}
              setSelectedProject={project => {
                setProject(project)
              }}
            />
          )}
          <div style={{ marginBottom: 30 }} />
        </div>
        <ApprovalsPeopleContainer>
          {weekBudget && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 20,
                width: '100%',
                marginBottom: 20,
              }}
            >
              <BudgetContainer>
                <Typography>
                  <strong>Project Budget (Week)</strong>
                </Typography>
                <Typography>{weekBudget}</Typography>
              </BudgetContainer>
              <BudgetContainer>
                <Typography>
                  <strong>Project Actual (Week)</strong>
                </Typography>
                <Typography>{actualUsed}</Typography>
              </BudgetContainer>
              <BudgetContainer>
                <Typography>
                  <strong> Pacing</strong>{' '}
                </Typography>
                <Typography>{actualPacing}%</Typography>
              </BudgetContainer>
            </div>
          )}

          <StyledWeekContainer data-testid="expenses.weekTabs">
            {availableWeeks.length > 0 &&
              availableWeeks.map(week => {
                return (
                  <WeekTabButton
                    key={week.stringWeek}
                    data-testid={`expenses.weekTab.${week.stringWeek.replace(/\s+/g, '-')}`}
                    minWidth="200px"
                    onClick={() => {
                      setSelectedWeek(week)
                    }}
                    selected={week.stringWeek === selectedWeek?.stringWeek}
                  >
                    {week.stringWeek}
                  </WeekTabButton>
                )
              })}
          </StyledWeekContainer>

          <WeekContainer>
            <TableWrapper>
              <TableContainer component={ProjectsTableContainer} rows={5}>
                <Table aria-label="simple table">
                  <StyledTableHead>
                    <TableRow>
                      <TableCell align="Left">Staff</TableCell>
                      <TableCell align="center">Description</TableCell>
                      <TableCell align="center">Category</TableCell>
                      <TableCell align="center">Cost</TableCell>
                      <TableCell align="center">Date</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </StyledTableHead>
                  {selectedWeekPendingExpenses.map(expense => {
                    const expenseKey = expense._id || expense.quickbooksId
                    return (
                      <TableBody key={expenseKey}>
                        <TableRow data-testid={`expenses.row.${expenseKey}`}>
                          <TableCell align="left">{people[expense.personId]?.fullName}</TableCell>
                          <TableCell align="center">{expense.memo}</TableCell>
                          <TableCell align="center">{expense.category}</TableCell>
                          <TableCell align="center">{expense.amount}</TableCell>
                          <TableCell align="center">
                            {new Date(expense.incurredDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell align="center">
                            <DetailsActionContainer>
                              <StyledApproveButton
                                data-testid={`expenses.approveBtn.${expenseKey}`}
                                onClick={() => {
                                  onApproveExpenseBillingStatus(
                                    expense.quickbooksId,
                                    'Billable',
                                    person._id,
                                  )
                                }}
                              >
                                Approve
                              </StyledApproveButton>
                              <StyledMutedButton
                                data-testid={`expenses.rejectBtn.${expenseKey}`}
                                onClick={() => {
                                  onApproveExpenseBillingStatus(
                                    expense.quickbooksId,
                                    'NotBillable',
                                    person._id,
                                  )
                                }}
                              >
                                Reject
                              </StyledMutedButton>
                            </DetailsActionContainer>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )
                  })}
                </Table>
              </TableContainer>
            </TableWrapper>
          </WeekContainer>
        </ApprovalsPeopleContainer>
      </ApprovalViewContainer>
    </ApprovalsContainer>
  )
}

ExpensesView.propTypes = {
  projects: PropTypes.arrayOf(PropTypes.object),
  client: PropTypes.object.isRequired,
  onEntryUpdate: PropTypes.func.isRequired,
  onUpdateManyEntries: PropTypes.func.isRequired,
  timeEntries: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  availableWeeks: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  people: PropTypes.object,
  timesheets: PropTypes.object,
  person: PropTypes.object,
  onWritteOff: PropTypes.func,
  onReject: PropTypes.func,
  rates: PropTypes.object,
  calendarValues: PropTypes.object,
  calendarResume: PropTypes.object,
  onSplitEntry: PropTypes.func,
  onProjectSelect: PropTypes.func,
}

export default ExpensesView
